import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  UserPlus,
  Search,
  Filter,
  Edit,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  UserX,
  UserCheck,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import ContentLoader from "../authentication/ContentLoader";
import { useToast } from "@/components/ui/use-toast";
import { db, auth, functions } from "../firebase/config";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import Header from "./Header";
import AdminSidebar from "./Sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import TeacherRegistrationForm from "@/components/forms/TeacherRegistrationForm";
import TeacherEditForm from "@/components/forms/TeacherEditForm";
import ManagePrivileges from "./ManagePrivileges";



// Declare process.env types
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      NODE_ENV: "development" | "production" | "test";
    }
  }
}

interface Teacher {
  id: string;
  employeeId: string;
  title?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  secondaryPhone?: string;
  address: string;
  dateOfBirth: string;
  nationalId: string;
  position: string[];
  tscNumber?: string;
  kraPin?: string;
  profilePicture: string | null;
  documents: string[];
  suspended?: boolean;
}

// ... existing code ...




interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

const TeachersManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<"list" | "register" | "edit" | "privileges">("list");
  const [viewMode, setViewMode] = useState<"card" | "list">("list");
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [schoolID, setSchoolID] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteEmployeeId, setConfirmDeleteEmployeeId] = useState<string | null>(null);
  const [deleteInput, setDeleteInput] = useState("");
  const [isSuspending, setIsSuspending] = useState(false);
  const [confirmSuspendTeacher, setConfirmSuspendTeacher] = useState<Teacher | null>(null);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(true);

  /* ───── Auth & Claims Verification ───── */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        console.log("TeacherManagement: No user found, deferring to App.tsx routing");
        setIsAuthLoading(false);
        return;
      }

      try {
        const token = await getIdTokenResult(user, true);
        const claims = token.claims as CustomClaims;
        const affiliations: Affiliation[] = claims.affiliations || [];
        const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
        const claimSchoolID: string | undefined = claims.schoolID;

        if (process.env.NODE_ENV !== "production") {
          console.log("Claims:", { affiliations, defaultAff, claimSchoolID });
        }

        const adminAff = defaultAff?.role === "admin" && defaultAff.schoolID
          ? defaultAff
          : affiliations.find((aff) => aff.role === "admin" && aff.schoolID);

        if (adminAff && adminAff.schoolID === claimSchoolID) {
          setSchoolID(adminAff.schoolID!);
          setHasValidClaims(true);
        } else {
          if (process.env.NODE_ENV !== "production") console.log("Retrying claims after 10s...");
          await new Promise((resolve) => setTimeout(resolve, 10000));
          const retryToken = await getIdTokenResult(user, true);
          const retryClaims = retryToken.claims as CustomClaims;
          const retryAffiliations: Affiliation[] = retryClaims.affiliations || [];
          const retryDefaultAff: Affiliation | undefined = retryClaims.defaultAffiliation;
          const retrySchoolID: string | undefined = retryClaims.schoolID;

          const retryAdminAff = retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
            ? retryDefaultAff
            : retryAffiliations.find((aff) => aff.role === "admin" && aff.schoolID);

          if (retryAdminAff && retryAdminAff.schoolID === retrySchoolID) {
            setSchoolID(retryAdminAff.schoolID!);
            setHasValidClaims(true);
          } else {
            throw new Error("Invalid admin claims");
          }
        }
      } catch (error: any) {
        console.error("Claims validation error:", error);
        // Check if it's a network error
        const isNetworkError = error?.code === 'auth/network-request-failed' ||
                              error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                              error?.message?.includes('network-request-failed') ||
                              error?.message?.includes('network');
        
        if (isNetworkError) {
          // Don't logout on network errors, just show a warning
          toast({
            title: "Network Error",
            description: "Unable to verify permissions due to network issues. Please check your connection.",
            variant: "destructive",
            duration: 5000,
          });
        } else {
          // Only logout for non-network errors
          toast({
            title: "Missing Permissions",
            description: "You lack permissions to manage teachers.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/login-page");
        }
      }
      setIsAuthLoading(false);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") console.log("Unsubscribing auth");
      unsubscribe();
    };
  }, [toast, navigate]);

  /* ───── Fetch Teachers ───── */
  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    setIsLoadingTeachers(true);

    const teachersCollectionRef = collection(db, `schools/${schoolID}/teachers`);
    const unsubscribe = onSnapshot(
      teachersCollectionRef,
      (snapshot) => {
        const teachersList: Teacher[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            employeeId: data.employeeId ?? "",
            firstName: data.firstName ?? "",
            middleName: data.middleName ?? undefined,
            lastName: data.lastName ?? "",
            email: data.email ?? "",
            phone: data.phoneNumber ?? "",
            secondaryPhone: data.secondaryPhone ?? undefined,
            address: data.address ?? "",
            dateOfBirth: data.dateOfBirth ?? "",
            nationalId: data.nationalId ?? "",
            position: Array.isArray(data.teacherType) ? data.teacherType : (typeof data.teacherType === 'string' && data.teacherType ? [data.teacherType] : []),
            tscNumber: data.tscNumber ?? undefined,
            kraPin: data.kraPin ?? undefined,
            profilePicture: data.profilePictureUrl ?? null,
            documents: data.documentUrls ?? [],
            suspended: data.suspended ?? false,
          } as Teacher;
        });
        setTeachers(teachersList);
        setIsLoadingTeachers(false);
        setCurrentPage(1); // Reset to first page on data change
      },
      (error) => {
        setIsLoadingTeachers(false);
        console.error("Error fetching teachers:", error);
        toast({
          title: error.code === "permission-denied" ? "Permission Error" : "Error Loading Teachers",
          description: error.code === "permission-denied"
            ? "Insufficient permissions to view teachers."
            : "Failed to fetch teachers. Please check your connection or data.",
          variant: "destructive",
          duration: 5000,
        });
      }
    );

    return () => {
      if (process.env.NODE_ENV !== "production") console.log("Unsubscribing teachers listener");
      unsubscribe();
    };
  }, [schoolID, isAuthLoading, hasValidClaims, toast]);

  /* ───── Handlers ───── */
  const handleRegisterTeacher = () => {
    setActiveView("register");
  };

  const handleEditTeacher = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setActiveView("edit");
  };

  const handleSuspendTeacher = async (teacher: Teacher) => {
    if (!schoolID || !hasValidClaims) return;
    
    setConfirmSuspendTeacher(teacher);
  };

  const handleDeleteTeacher = async (teacherId: string, employeeId: string) => {
    setConfirmDeleteId(teacherId);
    setConfirmDeleteEmployeeId(employeeId);
    setDeleteInput("");
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId || !confirmDeleteEmployeeId || deleteInput !== confirmDeleteEmployeeId) {
      toast({
        title: "Invalid Employee ID",
        description: "The entered Employee ID does not match.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    if (!schoolID || !hasValidClaims || !auth.currentUser) {
      toast({
        title: "Authentication Error",
        description: "Missing permissions or login.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    try {
      const token = await getIdTokenResult(auth.currentUser, true);
      const claims = token.claims as CustomClaims;
      const affiliations: Affiliation[] = claims.affiliations || [];
      const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
      const claimSchoolID: string | undefined = claims.schoolID;

      if (process.env.NODE_ENV !== "production") console.log("Delete claims:", { affiliations, defaultAff, claimSchoolID });

      const adminAff = defaultAff?.role === "admin" && defaultAff.schoolID === schoolID
        ? defaultAff
        : affiliations.find((aff) => aff.role === "admin" && aff.schoolID === schoolID);

      if (!adminAff) {
        if (process.env.NODE_ENV !== "production") console.log("Retrying claims after 10s...");
        await new Promise((resolve) => setTimeout(resolve, 10000));
        const retryToken = await getIdTokenResult(auth.currentUser, true);
        const retryClaims = retryToken.claims as CustomClaims;
        const retryAffiliations: Affiliation[] = retryClaims.affiliations || [];
        const retryDefaultAff: Affiliation | undefined = retryClaims.defaultAffiliation;

        if (
          !(retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID === schoolID) &&
          !retryAffiliations.find((aff) => aff.role === "admin" && aff.schoolID === schoolID)
        ) {
          throw new Error("Invalid admin claims");
        }
      }

      const deleteFn = httpsCallable(functions, 'deleteTeacher');
      await deleteFn({
        schoolID,
        teacherId: confirmDeleteId
      });

      // Optimistic update not strictly needed as onSnapshot will handle it, 
      // but we can clear the local state
      setConfirmDeleteId(null);
      setConfirmDeleteEmployeeId(null);
      setDeleteInput("");
      toast({
        title: "Teacher Deleted",
        description: "Teacher and their access have been successfully removed.",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error deleting teacher:", error);
      toast({
        title: error.code === "permission-denied" ? "Permission Error" : "Deletion Failed",
        description: error.code === "permission-denied"
          ? "Insufficient permissions to delete teacher."
          : error.message || "Failed to delete teacher.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleSaveTeacher = async (updatedTeacher: Teacher) => {
    if (!schoolID || !hasValidClaims || !auth.currentUser) {
      toast({
        title: "Authentication Error",
        description: "Missing permissions or login.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    try {
      const token = await getIdTokenResult(auth.currentUser, true);
      const claims = token.claims as CustomClaims;
      const affiliations: Affiliation[] = claims.affiliations || [];
      const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
      const claimSchoolID: string | undefined = claims.schoolID;

      if (process.env.NODE_ENV !== "production") console.log("Save claims:", { affiliations, defaultAff, claimSchoolID });

      const adminAff = defaultAff?.role === "admin" && defaultAff.schoolID === schoolID
        ? defaultAff
        : affiliations.find((aff) => aff.role === "admin" && aff.schoolID === schoolID);

      if (!adminAff) {
        if (process.env.NODE_ENV !== "production") console.log("Retrying claims after 10s...");
        await new Promise((resolve) => setTimeout(resolve, 10000));
        const retryToken = await getIdTokenResult(auth.currentUser, true);
        const retryClaims = retryToken.claims as CustomClaims;
        const retryAffiliations: Affiliation[] = retryClaims.affiliations || [];
        const retryDefaultAff: Affiliation | undefined = retryClaims.defaultAffiliation;

        if (
          !(retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID === schoolID) &&
          !retryAffiliations.find((aff) => aff.role === "admin" && aff.schoolID === schoolID)
        ) {
          throw new Error("Invalid admin claims");
        }
      }

      const teacherData: Teacher = {
        id: updatedTeacher.id,
        employeeId: updatedTeacher.employeeId,
        title: updatedTeacher.title ?? undefined,
        firstName: updatedTeacher.firstName,
        middleName: updatedTeacher.middleName ?? undefined,
        lastName: updatedTeacher.lastName,
        email: updatedTeacher.email,
        phone: updatedTeacher.phone,
        secondaryPhone: updatedTeacher.secondaryPhone ?? undefined,
        address: updatedTeacher.address,
        dateOfBirth: updatedTeacher.dateOfBirth,
        nationalId: updatedTeacher.nationalId,
        position: updatedTeacher.position,
        tscNumber: updatedTeacher.tscNumber ?? undefined,
        kraPin: updatedTeacher.kraPin ?? undefined,
        profilePicture: updatedTeacher.profilePicture ?? null,
        documents: updatedTeacher.documents ?? [],
      };
      // Clean data to remove undefined values
      const cleanData = (data: any): any => {
        const cleaned: any = {};
        for (const [key, value] of Object.entries(data)) {
          if (value !== undefined) {
            cleaned[key] = value;
          }
        }
        return cleaned;
      };

      await setDoc(doc(db, `schools/${schoolID}/teachers`, updatedTeacher.id), cleanData({
        employeeId: teacherData.employeeId,
        title: teacherData.title,
        firstName: teacherData.firstName,
        middleName: teacherData.middleName,
        lastName: teacherData.lastName,
        email: teacherData.email,
        phoneNumber: teacherData.phone,
        secondaryPhone: teacherData.secondaryPhone,
        address: teacherData.address,
        dateOfBirth: teacherData.dateOfBirth,
        nationalId: teacherData.nationalId,
        teacherType: teacherData.position,
        tscNumber: teacherData.tscNumber,
        kraPin: teacherData.kraPin,
        profilePictureUrl: typeof teacherData.profilePicture === 'string' ? teacherData.profilePicture : null,
        documentUrls: Array.isArray(teacherData.documents) 
          ? teacherData.documents.filter((d): d is string => typeof d === 'string') 
          : [],
      }));
      setTeachers(teachers.map((t) => (t.id === updatedTeacher.id ? teacherData : t)));
      setActiveView("list");
      setSelectedTeacher(null);
      toast({
        title: "Teacher Updated",
        description: "Teacher information has been successfully updated.",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error updating teacher:", error);
      toast({
        title: error.code === "permission-denied" ? "Permission Error" : "Update Failed",
        description: error.code === "permission-denied"
          ? "Insufficient permissions to update teacher."
          : error.message || "Failed to update teacher.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleBack = () => {
    setActiveView("list");
    setSelectedTeacher(null);
  };

  const getPositionBadge = (pos: string): string => {
    const colors: Record<string, string> = {
      teacher: "bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] text-white",
      librarian: "bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white",
      lab_technician: "bg-cyan-600 dark:bg-cyan-500 hover:bg-cyan-700 dark:hover:bg-cyan-600 text-white",
      "lab technician": "bg-cyan-600 dark:bg-cyan-500 hover:bg-cyan-700 dark:hover:bg-cyan-600 text-white",
      bursar: "bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 text-white",
      "head of department": "bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 text-white",
      head_of_department: "bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 text-white",
      "deputy principal": "bg-amber-600 dark:bg-amber-500 hover:bg-amber-700 dark:hover:bg-amber-600 text-white",
      deputy_principal: "bg-amber-600 dark:bg-amber-500 hover:bg-amber-700 dark:hover:bg-amber-600 text-white",
      principal: "bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 text-white",
      "other staff": "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white",
      other_staff: "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white",
    };
    return colors[pos.toLowerCase()] ?? colors["other staff"];
  };

  /* ───── Pagination Logic ───── */
  const filteredTeachers = teachers.filter((teacher) =>
    `${teacher.firstName} ${teacher.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTeachers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedTeachers = filteredTeachers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page
  };

  if (isAuthLoading || !hasValidClaims) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto { font-family: 'Roboto', sans-serif; }
        `}</style>
        <div className="fixed inset-0 bg-gray-50 dark:bg-gray-900 overflow-hidden font-roboto">
          <SidebarProvider className="h-full overflow-hidden">
            <div className="flex h-full w-full">
              <AdminSidebar onSectionChange={() => {}} />
              <div className="flex flex-col h-full flex-1 min-w-0">
                <Header />
                <main className="flex-1 min-h-0 overflow-auto p-3 sm:p-6 flex flex-col justify-center items-center">
                  <ContentLoader isLoading={true} message=""><div /></ContentLoader>
                </main>
              </div>
            </div>
          </SidebarProvider>
        </div>
      </>
    );
  }

  if (activeView === "register") {
    return <TeacherRegistrationForm onBack={handleBack} />;
  }

  if (activeView === "edit" && selectedTeacher) {
    return (
      <TeacherEditForm
        teacher={selectedTeacher}
        onBack={handleBack}
        onSave={handleSaveTeacher}
      />
    );
  }

  if (activeView === "privileges") {
    return <ManagePrivileges onBack={handleBack} schoolID={schoolID} />;
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
        .font-roboto { font-family: 'Roboto', sans-serif; }
      `}</style>
      <div className="fixed inset-0 bg-gray-50 dark:bg-gray-900 overflow-hidden font-roboto">
        <SidebarProvider className="h-full overflow-hidden">
          <div className="flex h-full w-full">
            <AdminSidebar onSectionChange={() => {}} />
            <div className="flex flex-col h-full flex-1 min-w-0">
              <Header />
              <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
             <ContentLoader isLoading={isLoadingTeachers} message="">
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-black dark:text-white mb-2">Teachers & Staff Management</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Manage school personnel and their information</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button
                    onClick={handleRegisterTeacher}
                    className="w-full sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10 flex items-center justify-center gap-2"
                    variant="outline"
                  >
                    <UserPlus className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">Register Teacher/Staff</span>
                  </Button>
                  <Button
                    onClick={() => setActiveView("privileges")}
                    className="w-full sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10 flex items-center justify-center gap-2"
                    variant="outline"
                  >
                    <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">Manage Privileges</span>
                  </Button>
                  <Button
                    onClick={() => setViewMode(viewMode === "card" ? "list" : "card")}
                    className="w-full sm:w-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10 flex items-center justify-center p-0"
                    variant="outline"
                    title={viewMode === "card" ? "Switch to list view" : "Switch to card view"}
                  >
                    {viewMode === "card" ? (
                      <List className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <LayoutGrid className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="sm:hidden ml-2">{viewMode === "card" ? "List View" : "Grid View"}</span>
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="relative flex-1 flex items-center">
                  <Search className="absolute left-3 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search by name or employee ID..."
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 rounded-md bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 leading-normal"
                    autoComplete="off"
                  />
                </div>
                <Button
                  variant="outline"
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10 flex items-center justify-center gap-2"
                >
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  Filter
                </Button>
              </div>
            </div>

            {teachers.length === 0 && (
              <div className="text-center py-12">
                <UserPlus className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  No teachers registered. Click 'Register Teacher/Staff' to add one.
                </p>
              </div>
            )}

            {viewMode === "card" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedTeachers.map((teacher) => (
                  <Card
                    key={teacher.id}
                    className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929] transition-colors"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-4">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={teacher.profilePicture ?? ""} />
                          <AvatarFallback>
                            {teacher.firstName[0]}{teacher.lastName[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-black dark:text-white truncate">
                            {teacher.firstName} {teacher.lastName}
                          </h3>
                          <p className="text-xs text-muted-foreground">ID: {teacher.employeeId}</p>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {teacher.position.map((pos, idx) => (
                              <Badge 
                                key={idx}
                                className={`text-xs px-2 py-0.5 font-medium whitespace-nowrap ${getPositionBadge(pos)}`}
                              >
                                {pos.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                              </Badge>
                            ))}
                            {teacher.suspended && (
                              <Badge variant="destructive" className="text-xs px-2 py-0.5">
                                Suspended
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Mail className="h-4 w-4" />
                          <span className="truncate">{teacher.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Phone className="h-4 w-4" />
                          <span>{teacher.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <MapPin className="h-4 w-4" />
                          <span className="truncate">{teacher.address}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Calendar className="h-4 w-4" />
                          <span>Born: {new Date(teacher.dateOfBirth).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex justify-end gap-0.5 mt-4 pt-4 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditTeacher(teacher)}
                          className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10 flex items-center gap-1"
                        >
                          <Edit className="h-4 w-4 text-muted-foreground" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSuspendTeacher(teacher)}
                          disabled={isSuspending}
                          className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-orange-600 dark:text-orange-400 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10 flex items-center gap-1"
                        >
                          {teacher.suspended ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                          {teacher.suspended ? "Unsuspend" : "Suspend"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteTeacher(teacher.id, teacher.employeeId)}
                          className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-[#EF4444] dark:text-[#F87171] hover:bg-gray-50 dark:hover:bg-[#131E31] hover:text-[#DC2626] dark:hover:text-[#EF4444] h-10 flex items-center gap-1"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                          <TableHead className="min-w-[150px] text-black dark:text-white">Name</TableHead>
                          <TableHead className="w-[120px] text-black dark:text-white">Employee ID</TableHead>
                          <TableHead className="hidden md:table-cell w-[150px] text-black dark:text-white">Email</TableHead>
                          <TableHead className="hidden md:table-cell w-[120px] text-black dark:text-white">Phone</TableHead>
                          <TableHead className="w-[100px] text-black dark:text-white">Position</TableHead>
                          <TableHead className="w-[100px] text-right text-black dark:text-white">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedTeachers.map((teacher) => (
                          <TableRow
                            key={teacher.id}
                            className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]"
                          >
                            <TableCell className="py-2 text-black dark:text-white">
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={teacher.profilePicture ?? ""} />
                                  <AvatarFallback>
                                    {teacher.firstName[0]}{teacher.lastName[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium text-sm">{teacher.firstName} {teacher.lastName}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-2 text-xs text-black dark:text-white">{teacher.employeeId}</TableCell>
                            <TableCell className="hidden md:table-cell py-2 text-xs text-black dark:text-white">{teacher.email}</TableCell>
                            <TableCell className="hidden md:table-cell py-2 text-xs text-black dark:text-white">{teacher.phone}</TableCell>
                            <TableCell className="py-2">
                              <div className="flex flex-col gap-1 items-start">
                                {teacher.position.map((pos, idx) => (
                                  <Badge 
                                    key={idx}
                                    className={`text-xs px-2 py-0.5 font-medium whitespace-nowrap ${getPositionBadge(pos)}`}
                                  >
                                    {pos.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                  </Badge>
                                ))}
                                {teacher.suspended && (
                                  <Badge variant="destructive" className="w-fit text-[10px] px-1 py-0">
                                    Suspended
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-2 text-right">
                              <div className="flex gap-0.5 justify-end items-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditTeacher(teacher)}
                                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-8 w-8 p-0"
                                  title="Edit teacher"
                                >
                                  <Edit className="h-4 w-4 text-muted-foreground" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSuspendTeacher(teacher)}
                                  disabled={isSuspending}
                                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-orange-600 dark:text-orange-400 hover:bg-gray-50 dark:hover:bg-[#131E31] h-8 w-8 p-0"
                                  title={teacher.suspended ? "Unsuspend teacher" : "Suspend teacher"}
                                >
                                  {teacher.suspended ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteTeacher(teacher.id, teacher.employeeId)}
                                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-[#EF4444] dark:text-[#F87171] hover:bg-gray-50 dark:hover:bg-[#131E31] hover:text-[#DC2626] dark:hover:text-[#EF4444] h-8 w-8 p-0"
                                  title="Delete teacher"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {filteredTeachers.length > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Rows per page:</span>
                  <Select
                    value={rowsPerPage.toString()}
                    onValueChange={handleRowsPerPageChange}
                  >
                    <SelectTrigger className="w-[80px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      if (totalPages <= 5) return true;
                      if (page === 1 || page === totalPages) return true;
                      if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                      return false;
                    })
                    .map((page) => (
                      <Button
                        key={page}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={`h-8 w-8 p-0 ${
                          currentPage === page
                            ? "bg-[#2563EB] dark:bg-[#3B82F6] text-white border-[#2563EB] dark:border-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB]"
                            : "bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]"
                        }`}
                      >
                        {page}
                      </Button>
                    ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
             </ContentLoader>
              </main>
            </div>
          </div>

          {/* Deletion Confirmation Dialog */}
          {/* Suspend/Restore Confirmation Popup */}
          <Dialog
            open={!!confirmSuspendTeacher}
            onOpenChange={(open) => {
              if (!open) setConfirmSuspendTeacher(null);
            }}
          >
            <DialogContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] font-roboto max-w-md rounded-md flex flex-col items-center justify-center">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-black dark:text-white text-center">
                  {confirmSuspendTeacher?.suspended
                    ? "Restore Access"
                    : "Confirm Suspension"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-center">
                {confirmSuspendTeacher?.suspended ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Restoring access will allow this teacher to log in again.<br />
                    Are you sure you want to restore access?
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Suspending this account will temporarily revoke their access.<br />
                    Are you sure you want to proceed?
                  </p>
                )}
              </div>
              <DialogFooter className="flex justify-center gap-4 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmSuspendTeacher(null)}
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={async () => {
                    if (!confirmSuspendTeacher || !schoolID || !hasValidClaims) return;
                    setIsSuspending(true);
                    try {
                      const suspendFn = httpsCallable(functions, 'suspendTeacher');
                      await suspendFn({
                        schoolID,
                        teacherId: confirmSuspendTeacher.id,
                        suspended: !confirmSuspendTeacher.suspended
                      });
                      toast({
                        title: confirmSuspendTeacher.suspended ? "Teacher Unsuspended" : "Teacher Suspended",
                        description: `Teacher access has been ${confirmSuspendTeacher.suspended ? "restored" : "revoked"}.`,
                        duration: 3000,
                      });
                      setConfirmSuspendTeacher(null);
                    } catch (error: any) {
                      console.error("Error suspending teacher:", error);
                      toast({
                        title: "Action Failed",
                        description: error.message || "Failed to update teacher status.",
                        variant: "destructive",
                        duration: 5000,
                      });
                    } finally {
                      setIsSuspending(false);
                    }
                  }}
                  disabled={isSuspending}
                  className={confirmSuspendTeacher?.suspended
                    ? "bg-[#22C55E] dark:bg-[#4ADE80] text-white hover:bg-[#16A34A] dark:hover:bg-[#22C55E] h-10 w-32"
                    : "bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] h-10 w-32"}
                >
                  {isSuspending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    confirmSuspendTeacher?.suspended ? "Restore Access" : "Understood"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog
            open={!!confirmDeleteId}
            onOpenChange={(open) => {
              if (!open) {
                setConfirmDeleteId(null);
                setConfirmDeleteEmployeeId(null);
                setDeleteInput("");
              }
            }}
          >
            <DialogContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] font-roboto max-w-md rounded-md">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-black dark:text-white">Confirm Teacher Deletion</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  To delete this teacher, enter their Employee ID: <strong>{confirmDeleteEmployeeId}</strong>
                </p>
                <Input
                  placeholder="Enter Employee ID"
                  value={deleteInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeleteInput(e.target.value)}
                  className="pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white h-10 rounded-md"
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setConfirmDeleteId(null);
                    setConfirmDeleteEmployeeId(null);
                    setDeleteInput("");
                  }}
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={confirmDelete}
                  className="bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] h-10"
                >
                  Confirm
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </SidebarProvider>
      </div>
    </>
  );
};

export default TeachersManagement;